// Validation utility functions for forms

export const validators = {
    // Email validation
    email: (value) => {
        if (!value) return { valid: true, message: '' };
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return {
            valid: emailRegex.test(value),
            message: emailRegex.test(value) ? '' : 'Please enter a valid email address'
        };
    },

    // Phone number validation (Qatar format)
    phone: (value) => {
        if (!value) return { valid: false, message: 'Phone number is required' };
        // Qatar phone: +974 followed by 8 digits, or just 8 digits
        const phoneRegex = /^(\+974)?[3456789]\d{7}$/;
        const cleanValue = value.replace(/[\s-]/g, '');
        return {
            valid: phoneRegex.test(cleanValue),
            message: phoneRegex.test(cleanValue) ? '' : 'Please enter a valid Qatar phone number (8 digits)'
        };
    },

    // Required field validation
    required: (value, fieldName = 'This field') => {
        const isValid = value && value.toString().trim().length > 0;
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} is required`
        };
    },

    // Text length validation
    minLength: (value, min, fieldName = 'This field') => {
        if (!value) return { valid: true, message: '' };
        const isValid = value.trim().length >= min;
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} must be at least ${min} characters`
        };
    },

    maxLength: (value, max, fieldName = 'This field') => {
        if (!value) return { valid: true, message: '' };
        const isValid = value.trim().length <= max;
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} must not exceed ${max} characters`
        };
    },

    // Number validation
    number: (value, fieldName = 'This field') => {
        if (!value) return { valid: true, message: '' };
        const isValid = !isNaN(value) && isFinite(value);
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} must be a valid number`
        };
    },

    positiveNumber: (value, fieldName = 'This field') => {
        if (!value) return { valid: true, message: '' };
        const num = parseFloat(value);
        const isValid = !isNaN(num) && num > 0;
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} must be a positive number`
        };
    },

    // Date validation
    date: (value, fieldName = 'This field') => {
        if (!value) return { valid: true, message: '' };
        const date = new Date(value);
        const isValid = date instanceof Date && !isNaN(date);
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} must be a valid date`
        };
    },

    futureDate: (value, fieldName = 'This field') => {
        if (!value) return { valid: true, message: '' };
        const date = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isValid = date >= today;
        return {
            valid: isValid,
            message: isValid ? '' : `${fieldName} cannot be in the past`
        };
    },

    // Cheque/Voucher number validation
    chequeNumber: (value) => {
        if (!value) return { valid: false, message: 'Cheque number is required' };
        const isValid = value.trim().length >= 3 && value.trim().length <= 20;
        return {
            valid: isValid,
            message: isValid ? '' : 'Cheque number must be between 3 and 20 characters'
        };
    },

    voucherNumber: (value) => {
        if (!value) return { valid: false, message: 'Voucher number is required' };
        const isValid = value.trim().length >= 3 && value.trim().length <= 20;
        return {
            valid: isValid,
            message: isValid ? '' : 'Voucher number must be between 3 and 20 characters'
        };
    },

    // Name validation
    name: (value, fieldName = 'Name') => {
        if (!value) return { valid: false, message: `${fieldName} is required` };
        const nameRegex = /^[a-zA-Z\s'-]{2,50}$/;
        return {
            valid: nameRegex.test(value.trim()),
            message: nameRegex.test(value.trim()) ? '' : `${fieldName} must be 2-50 characters and contain only letters, spaces, hyphens, and apostrophes`
        };
    },

    // Amount validation
    amount: (value) => {
        if (!value) return { valid: false, message: 'Amount is required' };
        const num = parseFloat(value);
        const isValid = !isNaN(num) && num > 0 && num <= 999999999;
        return {
            valid: isValid,
            message: isValid ? '' : 'Amount must be a positive number less than 1 billion'
        };
    }
};

// Validate entire form
export const validateForm = (formData, validationRules) => {
    const errors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(field => {
        const rules = validationRules[field];
        const value = formData[field];

        for (let rule of rules) {
            const result = rule(value);
            if (!result.valid) {
                errors[field] = result.message;
                isValid = false;
                break; // Stop at first error for this field
            }
        }
    });

    return { isValid, errors };
};

// Sanitize input
export const sanitizeInput = (value) => {
    if (typeof value !== 'string') return value;
    // Remove potentially dangerous characters
    return value
        .trim()
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<[^>]+>/g, '');
};
